import adiTrack from '@adi/adiTrack';
import docCookies from 'doc-cookies';
import sha256 from 'js-sha256';
import {adiGigyaData} from '@adi/gigya_js/src/data'; //replace window.advance.gigya with this
import {callCaleLogError} from './call_cale';
import state from "../state";

let closeTerm;
let checkoutComplete;
let pushedWallShown = false;

/**
 * make a label that's easier on the eyes than JSON.stringify
 * takes some data and an array of properties on that data that should get labelified.
 * @param {Object} data - data to labelify
 * @param {Array} arr - keys to iterate through, use all keys if not given
 * @return {String} label data
 */
const labelify = (data, arr) => {
    const arrVal = arr || Object.keys(data);
    const getProp = (prop) =>
        data[prop] ? `${prop}: ${JSON.stringify(data[prop])}` : ""; // return value if exists
    return arrVal.
        reduce( (a, b) => (getProp(b) ? `${a}${getProp(b)}||` : a), "").
        replace(/\|\|$/, ""); // return the cleaned string
};

/**
 * window.dataLayer is a ga thing
 * parses out the options.type from the options and use that taylor the data to that.
 * only send push if type is offer-selected, checkout-complete or new-user-registration
 * @param {Object} options - data object to send to data layer
 */
const dataLayerPush = (options = {}) => {
    const {
        price, type, adiOfferId, qty,
    } = options;
    const event = {
        "offer-selected": {
            event: "adiSubscribeClick",
            adiOfferId,
        },
        "checkout-complete": {
            event: "adiPaymentComplete",
            adiSubPrice: price && price.replace("$", ""),
            adiOfferId,
        },
        "new-user-registration": {
            event: "adiNewEmailReg",
        },
        "wall-shown": {
            event: "adiModalShown",
        },
    }[type];

    window.dataLayer = window.dataLayer || [];

    //window.adiData.affiliate === 'syracuse' will be removed in a few weeks .. 4/4/2019
    //sha256 encoded string used for bounceX
    if (type === "checkout-complete") {
        function getAccountInfoResponse (response) {
            if (response.status === "FAIL") {
                return;
            }
            const message = response.profile.email.trim().toLowerCase();
            sha256(message);
            const hash = sha256.create().update(message);
            event.emailSha56 = hash.hex();
            event && window.dataLayer.push(event);
            window.postMessage({
                topic: 'purchase', amount: event.adiSubPrice, qty,
            }, "*");
        }
        window.gigya.accounts.getAccountInfo({callback: getAccountInfoResponse});
    } else {
    // only push this once
        if (type === 'wall-shown') {
            if (pushedWallShown) {
                return;
            }
            pushedWallShown = true;
            window.postMessage("paywall-modal-shown", "*");
        }
        event && window.dataLayer.push(event);
    }
};

const logPrivateMode = () => {
    const affiliates = {
        affiliateList: [ "all" ],
    };

    const props = {
        action: `incognito`,
        category: "paywall",
        label: "isPrivate: true",
    };

    adiTrack.event(props, affiliates);
};

/**
 * analytics dispatcher - push mather, ga and datalayer events
 * @param {Object} data - data to be processed. mutates data object by adding `eventname: type`
 * @param {string} type - action of the data
 */
const analytics = (data, type) => {
    window._matherq = window._matherq || [];

    window._matherq.push([
        "paywallEvent",
        data,
        {
            userId:
        Advance.Gigya.auth &&
        Advance.Gigya.auth.user &&
        Advance.Gigya.auth.user.UID,
        }
    ]);

    const affiliates = {
        affiliateList: [ "all" ],
    };

    /**
   * get label for ga
   * @returns a string with 'eventname', 'action', 'category', 'type', 'offers' properties seperated by ||
   */
    const getLabel = () => {
        return labelify(Object.assign({}, data, {eventname: type}), [
            "eventname",
            "action",
            "category",
            "type",
            "offers"
        ]);
    };

    const props = {
        action: `modal_shown`,
        category: "paywall",
        label: getLabel(),
    };

    adiTrack.event(props, affiliates);
    // call the ga dataLayerPush with some portions of the data
    dataLayerPush({
        price: data.offers && data.offers.price,
        type,
        adiOfferId: data.offers && data.offers.offerId,
        qty: data.offers && data.offers.qty,
    });

    type !== "close_term" &&
    docCookies.setItem("meter_progression", type, Infinity, "/");
};

let wallShown = false;
let queueOfferShownEvent = false;

// get and process data from GrandPiano iFrame message
const getOffers = (data) => {
    const getOffer = () => {
        const getTerm = (term = {}) => {
            const getQty = () => {
                if (term.firstPeriod) {
                    const duration = term.firstPeriod.match('year') ? 12 : 1;
                    return Number(term.firstPeriod.match(/\d+/)[0]) * duration;
                }
                return 1;
            };
            const billingTable = term.billingPlanTable;

            const currentBill = billingTable ?
                billingTable[billingTable.length - 1] :
                {};
            const currentTerm = Object.keys(currentBill).length ?
                currentBill.billingPeriod.split(" ") :
                [];

            return {
                activeMeters:
                    data.piano.activeMeters &&
                    data.piano.activeMeters.map( (meter) => labelify(meter) ),
                offerId: data.piano.offerId, // OFFER ID offerId (comes from iframe JS call)
                description: term.description,
                name: term.name,
                price: currentBill.price, // TERM term.chargeAmount (comes from iframe JS call)
                per: currentBill.billingPeriod, // Interval, e.g. month (comes from iframe JS call)
                termAmount: currentTerm[0], // Interval Length, e.g. 4 (comes from iframe JS call)
                termUnit: currentTerm[1], // Interval, e.g. month (comes from iframe JS call)
                displayMode: data.piano.displayMode, // "modal" or "inline" (comes from iframe JS call)
                templateId: data.piano.templateId, // (comes from iframe JS call)
                templateVariantId: data.piano.templateVariantId,
                experienceId: data.piano.experienceId,
                qty: getQty(),
            };
        };
        return data.piano.selectedTerm &&
      Object.keys(data.piano.selectedTerm).length ?
            getTerm(data.piano.selectedTerm) :
            data.piano.terms ?
                data.piano.terms.map(getTerm) :
                getTerm();
    };
    // get a cleaned offer object
    try {
        const offers = getOffer();
        return Array.isArray(offers) && offers.length === 1 ? offers[0] : offers;
    } catch (e) {
        callCaleLogError({getOffer: e.message});
    }
    return false;
};

const pianoCallbacks = (data) => {
    const offers = getOffers(data);

    // types shojld ordered as an array
    // make xml

    const types = {
        "wall-shown": {
            action: "stop",
            category: "block",
            type: "paywall",
            offers,
        },

        // internal event
        "soft-wall-shown": {
            action: "modal",
            category: "block",
            offers,
        },

        // internal event
        "subscription-started": {
            action: "subscriptions_started",
            category: "payment",
            type: "subscription",
            offers,
        },

        "checkout-shown": {
            action: "checkout",
            category: "payment",
            offers,
        },

        "offer-selected": {
            action: "subscription_selected",
            category: "payment",
            offers,
        },
    };

    // when x button is pressed on modal
    closeTerm = {
        action: "abandoned",
        category: "payment",
        offers,
    };

    checkoutComplete = {
        action: "completed",
        category: "payment",
        offers,
    };

    // offer shown object
    const subscriptionsOffered = {
        action: "subscriptions_offered",
        category: "payment",
        offers,
    };

    const modalSubscriptionsStarted = {
        action: "subscriptions_started",
        category: "payment",
        type: "modal",
        offers,
    };

    let action = data.piano.action;

    // call modal-subscripteion-started when user returns from abandonment
    const softWallProgression = () => {
        if (docCookies.getItem("meter_progression") === "soft-wall-shown") {
            analytics(modalSubscriptionsStarted, "modal-subscription-started");
        }
    };

    // call subscriptions-offered comes after wall-shown
    if (action === "offer-shown") {
        if (wallShown) {
            queueOfferShownEvent = false;
            analytics(subscriptionsOffered, "subscriptions-offered");
            softWallProgression();
        } else {
            queueOfferShownEvent = true;
        }
    }

    if (types[action]) {

        state.progression = action;
        // when we get a 'wall-shown', then -> subscriptions-offered
        if (action === "wall-shown") {
            // dataLayerPush has a flag so for wall-shown (pushedWallShown) so this event can only be pushed once.
            dataLayerPush({type: action});

            const el = document.querySelector(".tp-close");
            const soft = !(el && getComputedStyle(el).display === "none");

            // regex matches where they are
            const fromRegPage =
        data.piano.isLandingPage ||
        Boolean(
            window.location.href.match(
                /\:\/\/[^/]+?\/(subscribe|piano|digitalsubscription|learnmore)/
            )
        );
            action = soft ?
                "soft-wall-shown" :
                fromRegPage ?
                    "subscription-started" :
                    action;

            wallShown = true;

            // if we got an offer-shown before the paywall, wait before dispatching the event one tick after we send a wall-shown event
            if (queueOfferShownEvent) {
                setTimeout(function () {
                    softWallProgression();
                    analytics(subscriptionsOffered, "subscriptions-offered");
                }, 1);
                queueOfferShownEvent = false;
            }
        }
        // if we have an action listed in the types dictionary, then call analytics with it.
        analytics(types[action], action);
    }
};

// listen for piano events
const setupAnalyticsListeners = () => {
    window.tp = window.tp || [];
    // from piano if user clicks the x button
    window.tp.push([
        "addHandler",
        "checkoutClose",
        (event) => event.state === "close" && analytics(closeTerm, "close-term")
    ]);

    // from piano if user clicks the x button
    window.tp.push([
        "addHandler",
        "checkoutComplete",
        () => analytics(checkoutComplete, "checkout-complete")
    ]);

    // gigya event
    window.addEventListener("newUserLogin.adi", () =>
        analytics(
            {
                category: "registration",
                action: "completed",
                type: "paywall",
            },
            "new-user-registration"
        )
    );

    //follow user across site
    const storeUserCookies = () =>
        docCookies.setItem("pageviewid", adiGigyaData.auth.user.UID);

    const removeUserCookies = () => docCookies.removeItem("pageviewid");

    // when gigya attempts to log someone in
    window.addEventListener("getAccountInfo.adi", storeUserCookies);

    // when user isnt logged in or logs out
    window.addEventListener("onLogout.adi", removeUserCookies);
    window.addEventListener("notLoggedIn.adi", removeUserCookies);
    window.addEventListener("noUser.adi", removeUserCookies);
};

const logCaleMessages = (data) =>
    docCookies.setItem("cale_results", `${data.response}||${data.message}`);

export {
    setupAnalyticsListeners,
    logCaleMessages,
    logPrivateMode,
    pianoCallbacks,
    getOffers,
};
